import axios from 'axios';
import { toast } from 'react-toastify';
import { applyTipObligationPaidUpdates } from './users';
import {
    buildPaidUpdatesFromDirectEmployeePayResult,
    resolvePaidUpdatesFromManagerRelease,
} from '../utils/tipPayoutPaidStatus';
import {
    buildTipPayoutDateQueryParams,
    buildTipPayoutDateRangeKey,
    buildTipPayoutDateRequestBody,
} from '../utils/tipPayoutDateFilter';

let tipPayoutSettingsRequestSeq = 0;
let tipPayoutDirectRecipientsRequestSeq = 0;
let activeTipPayoutSummaryRequestSignature = null;

const PAYOUT_API_TIMEOUT_MS = 120000;
const PAYOUT_FAST_PREVIEW_TIMEOUT_MS = 20000;
const PAYOUT_FULL_PREVIEW_BACKGROUND_DELAY_MS = 5000;

function withPayoutTimeout(config = {}, { fast = false } = {}) {
    const timeout = fast ? PAYOUT_FAST_PREVIEW_TIMEOUT_MS : PAYOUT_API_TIMEOUT_MS;
    // timeout last so it always wins over axios.defaults.timeout (30s) and any spread config.
    return { ...config, timeout };
}

function toReserveAmount(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value > 0 ? value : 0;
    }

    const normalized = String(value ?? '').replace(/[$,\s]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function normalizeReserveConfig(raw) {
    if (!raw) return null;
    return {
        merchantId: raw.merchantId ?? raw.MerchantId,
        flatReserveEnabled: !!(raw.flatReserveEnabled ?? raw.FlatReserveEnabled),
        flatReserveAmount: Number(raw.flatReserveAmount ?? raw.FlatReserveAmount ?? 0),
        percentReserveEnabled: !!(raw.percentReserveEnabled ?? raw.PercentReserveEnabled),
        percentReserveValue: Number(raw.percentReserveValue ?? raw.PercentReserveValue ?? 0),
        reserveTipsEnabled: !!(raw.reserveTipsEnabled ?? raw.ReserveTipsEnabled),
        reserveTaxEnabled: !!(raw.reserveTaxEnabled ?? raw.ReserveTaxEnabled),
        stripeHeldAmount: Number(raw.stripeHeldAmount ?? raw.StripeHeldAmount ?? 0),
        stripeHoldTargetAmount: Number(raw.stripeHoldTargetAmount ?? raw.StripeHoldTargetAmount ?? 0),
        stripeHoldEnforced: !!(raw.stripeHoldEnforced ?? raw.StripeHoldEnforced),
        stripeHoldSyncError: raw.stripeHoldSyncError ?? raw.StripeHoldSyncError ?? '',
        stripeHoldSyncedAtUtc: raw.stripeHoldSyncedAtUtc ?? raw.StripeHoldSyncedAtUtc ?? null,
    };
}

export const MERCHANT_PAYOUT_PREVIEW_RECEIVING = 'MERCHANT_PAYOUT_PREVIEW_RECEIVING';
export const MERCHANT_PAYOUT_PREVIEW_RECEIVED = 'MERCHANT_PAYOUT_PREVIEW_RECEIVED';
export const MERCHANT_PAYOUT_PREVIEW_ERROR = 'MERCHANT_PAYOUT_PREVIEW_ERROR';

export const MERCHANT_RESERVE_SAVING = 'MERCHANT_RESERVE_SAVING';
export const MERCHANT_RESERVE_SAVED = 'MERCHANT_RESERVE_SAVED';
export const MERCHANT_RESERVE_SAVE_ERROR = 'MERCHANT_RESERVE_SAVE_ERROR';
export const MERCHANT_RESERVE_RECEIVING = 'MERCHANT_RESERVE_RECEIVING';
export const MERCHANT_RESERVE_RECEIVED = 'MERCHANT_RESERVE_RECEIVED';
export const MERCHANT_RESERVE_LOAD_ERROR = 'MERCHANT_RESERVE_LOAD_ERROR';

export const MERCHANT_PAYOUT_CREATING = 'MERCHANT_PAYOUT_CREATING';
export const MERCHANT_PAYOUT_CREATED = 'MERCHANT_PAYOUT_CREATED';
export const MERCHANT_PAYOUT_CREATE_ERROR = 'MERCHANT_PAYOUT_CREATE_ERROR';
export const MERCHANT_PAYOUT_ERROR_CLEARED = 'MERCHANT_PAYOUT_ERROR_CLEARED';

export const MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVING = 'MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVING';
export const MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVED = 'MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVED';
export const MERCHANT_TIP_PAYOUT_SETTINGS_ERROR = 'MERCHANT_TIP_PAYOUT_SETTINGS_ERROR';
export const MERCHANT_TIP_PAYOUT_SETTINGS_SAVING = 'MERCHANT_TIP_PAYOUT_SETTINGS_SAVING';
export const MERCHANT_TIP_PAYOUT_SETTINGS_SAVED = 'MERCHANT_TIP_PAYOUT_SETTINGS_SAVED';
export const MERCHANT_TIP_PAYOUT_SETTINGS_SAVE_ERROR = 'MERCHANT_TIP_PAYOUT_SETTINGS_SAVE_ERROR';
export const MERCHANT_TIP_PAYOUT_PAYING = 'MERCHANT_TIP_PAYOUT_PAYING';
export const MERCHANT_TIP_PAYOUT_PAID = 'MERCHANT_TIP_PAYOUT_PAID';
export const MERCHANT_TIP_PAYOUT_PAY_ERROR = 'MERCHANT_TIP_PAYOUT_PAY_ERROR';
export const MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVING = 'MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVING';
export const MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVED = 'MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVED';
export const MERCHANT_TIP_PAYOUT_MARK_ALL_ERROR = 'MERCHANT_TIP_PAYOUT_MARK_ALL_ERROR';

export const MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVING = 'MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVING';
export const MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVED = 'MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVED';
export const MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_ERROR = 'MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_ERROR';
export const MERCHANT_TIP_PAYOUT_DIRECT_PAYING = 'MERCHANT_TIP_PAYOUT_DIRECT_PAYING';
export const MERCHANT_TIP_PAYOUT_DIRECT_PAID = 'MERCHANT_TIP_PAYOUT_DIRECT_PAID';
export const MERCHANT_TIP_PAYOUT_DIRECT_PAY_ERROR = 'MERCHANT_TIP_PAYOUT_DIRECT_PAY_ERROR';

export const MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVING = 'MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVING';
export const MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVED = 'MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVED';
export const MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_ERROR = 'MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_ERROR';
export const MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVING = 'MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVING';
export const MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVED = 'MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVED';
export const MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_ERROR = 'MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_ERROR';
export const MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVING = 'MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVING';
export const MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVED = 'MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVED';
export const MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_ERROR = 'MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_ERROR';
export const MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVING = 'MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVING';
export const MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVED = 'MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVED';
export const MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_ERROR = 'MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_ERROR';
export const MERCHANT_RECENT_TIP_PAYOUTS_RECEIVING = 'MERCHANT_RECENT_TIP_PAYOUTS_RECEIVING';
export const MERCHANT_RECENT_TIP_PAYOUTS_RECEIVED = 'MERCHANT_RECENT_TIP_PAYOUTS_RECEIVED';
export const MERCHANT_RECENT_TIP_PAYOUTS_ERROR = 'MERCHANT_RECENT_TIP_PAYOUTS_ERROR';

export const MERCHANT_RECENT_BANK_PAYOUTS_RECEIVING = 'MERCHANT_RECENT_BANK_PAYOUTS_RECEIVING';
export const MERCHANT_RECENT_BANK_PAYOUTS_RECEIVED = 'MERCHANT_RECENT_BANK_PAYOUTS_RECEIVED';
export const MERCHANT_RECENT_BANK_PAYOUTS_ERROR = 'MERCHANT_RECENT_BANK_PAYOUTS_ERROR';

const RECENT_SUCCESSFUL_TIP_PAYOUTS_LIMIT = 10;
export const RECENT_MERCHANT_BANK_PAYOUTS_LIMIT = 50;
export const RECENT_MERCHANT_BANK_PAYOUTS_PAGE_SIZE = 10;

function normalizeRecentMerchantBankPayoutRecord(raw) {
    if (!raw) return null;

    const status = String(raw.status ?? raw.Status ?? '').trim().toLowerCase();
    const failureCode = String(raw.failureCode ?? raw.FailureCode ?? '').trim();
    const failureMessage = String(raw.failureMessage ?? raw.FailureMessage ?? '').trim();
    let failureReason = String(raw.failureReason ?? raw.FailureReason ?? '').trim();
    if (!failureReason && status === 'failed') {
        if (failureMessage && failureCode) {
            failureReason = `${failureMessage} (${failureCode})`;
        } else {
            failureReason = failureMessage || failureCode || '';
        }
    }

    const methodRaw = String(raw.method ?? raw.Method ?? raw.payoutMethod ?? raw.PayoutMethod ?? 'standard')
        .trim()
        .toLowerCase();

    return {
        payoutId: raw.payoutId ?? raw.PayoutId ?? '',
        amount: Number(raw.amount ?? raw.Amount ?? 0) || 0,
        method: methodRaw === 'instant' ? 'instant' : 'standard',
        status: status || 'unknown',
        createdAtLocal: raw.createdAtLocal ?? raw.CreatedAtLocal ?? null,
        createdAtUtc: raw.createdAtUtc ?? raw.CreatedAtUtc ?? null,
        failureCode: failureCode || null,
        failureMessage: failureMessage || null,
        failureReason: failureReason || null,
    };
}

function normalizeRecentTipPayoutRecord(raw) {
    if (!raw) return null;

    const transferAmount = Number(
        raw.transferAmount
        ?? raw.TransferAmount
        ?? raw.totalAmount
        ?? raw.TotalAmount
        ?? 0
    );
    const remainingAmount = Number(raw.remainingAmount ?? raw.RemainingAmount ?? 0);
    const statusRaw = String(raw.status ?? raw.Status ?? '').trim().toLowerCase();
    let status = statusRaw;
    if (!status) {
        status = transferAmount > 0 && remainingAmount > 0.01 ? 'partial' : (transferAmount > 0 ? 'completed' : 'failed');
    }

    return {
        releaseId: raw.releaseId ?? raw.ReleaseId ?? '',
        recipientUserId: raw.recipientUserId ?? raw.RecipientUserId ?? null,
        recipientUsername: raw.recipientUsername ?? raw.RecipientUsername ?? raw.username ?? raw.Username ?? '',
        status,
        transferAmount: status === 'failed' ? 0 : transferAmount,
        remainingAmount,
        stripeTransferId: raw.stripeTransferId ?? raw.StripeTransferId ?? '',
        message: raw.message ?? raw.Message ?? '',
        attemptedAt: raw.attemptedAt ?? raw.AttemptedAt ?? raw.paidAt ?? raw.PaidAt ?? null,
        isBackfill: !!(raw.isBackfill ?? raw.IsBackfill),
    };
}

function normalizePendingTipObligationItem(raw) {
    if (!raw) return null;
    return {
        userId: raw.userId ?? raw.UserId,
        username: raw.username ?? raw.Username ?? '',
        employeeId: raw.employeeId ?? raw.EmployeeId ?? '',
        workPeriodId: raw.workPeriodId ?? raw.WorkPeriodId ?? null,
        workPeriodDate: raw.workPeriodDate ?? raw.WorkPeriodDate ?? null,
        amountOwed: Number(raw.amountOwed ?? raw.AmountOwed ?? 0),
    };
}

function normalizeTipsPaidToManagerItem(raw) {
    if (!raw) return null;
    return {
        userId: raw.userId ?? raw.UserId,
        username: raw.username ?? raw.Username ?? '',
        employeeId: raw.employeeId ?? raw.EmployeeId ?? '',
        workPeriodId: raw.workPeriodId ?? raw.WorkPeriodId ?? null,
        workPeriodDate: raw.workPeriodDate ?? raw.WorkPeriodDate ?? null,
        amountPaid: Number(raw.amountPaid ?? raw.AmountPaid ?? 0),
    };
}

function normalizeReleasableTipObligationItem(raw) {
    if (!raw) return null;
    return {
        userId: raw.userId ?? raw.UserId,
        username: raw.username ?? raw.Username ?? '',
        employeeId: raw.employeeId ?? raw.EmployeeId ?? '',
        workPeriodId: raw.workPeriodId ?? raw.WorkPeriodId ?? null,
        workPeriodDate: raw.workPeriodDate ?? raw.WorkPeriodDate ?? null,
        amountReleasable: Number(raw.amountReleasable ?? raw.AmountReleasable ?? 0),
        canPayDirect: !!(raw.canPayDirect ?? raw.CanPayDirect),
        tipPayoutEnabled: !!(raw.tipPayoutEnabled ?? raw.TipPayoutEnabled),
        stripeAccountStatus: raw.stripeAccountStatus ?? raw.StripeAccountStatus ?? '',
    };
}

function normalizeWorkPeriodBalance(raw) {
    if (!raw) return null;
    return {
        workPeriodId: raw.workPeriodId ?? raw.WorkPeriodId ?? null,
        workPeriodDate: raw.workPeriodDate ?? raw.WorkPeriodDate ?? null,
        amountOwed: Number(raw.amountOwed ?? raw.AmountOwed ?? 0),
        amountCleared: Number(raw.amountCleared ?? raw.AmountCleared ?? 0),
    };
}

function normalizeRecipient(raw) {
    if (!raw) return null;
    return {
        userId: raw.userId ?? raw.UserId,
        username: raw.username ?? raw.Username ?? '',
        employeeId: raw.employeeId ?? raw.EmployeeId ?? '',
        roleName: raw.roleName ?? raw.RoleName ?? '',
        stripeAccountId: raw.stripeAccountId ?? raw.StripeAccountId ?? '',
        stripeAccountStatus: raw.stripeAccountStatus ?? raw.StripeAccountStatus ?? '',
        tipPayoutEnabled: !!(raw.tipPayoutEnabled ?? raw.TipPayoutEnabled),
        clearedTips: Number(raw.clearedTips ?? raw.ClearedTips ?? 0),
        tipsOwed: Number(raw.tipsOwed ?? raw.TipsOwed ?? 0),
        workPeriodBalances: (raw.workPeriodBalances || raw.WorkPeriodBalances || [])
            .map(normalizeWorkPeriodBalance)
            .filter(Boolean),
    };
}

function normalizeTipPayoutSettings(raw) {
    if (!raw) return null;
    const hasEligible = raw.eligibleRecipients != null || raw.EligibleRecipients != null;
    const eligible = hasEligible
        ? (raw.eligibleRecipients || raw.EligibleRecipients || [])
            .map(normalizeRecipient)
            .filter(Boolean)
        : undefined;
    const hasDesignated = raw.designatedRecipient != null || raw.DesignatedRecipient != null;
    const designated = hasDesignated
        ? normalizeRecipient(raw.designatedRecipient || raw.DesignatedRecipient)
        : undefined;

    const reservedTipsAndGratuity = raw.reservedTipsAndGratuity ?? raw.ReservedTipsAndGratuity;
    const pendingTipsAndGratuity = raw.pendingTipsAndGratuity ?? raw.PendingTipsAndGratuity;
    const releasableTipsAndGratuity = raw.releasableTipsAndGratuity ?? raw.ReleasableTipsAndGratuity;
    const tipsAndGratuityPaidToManager = raw.tipsAndGratuityPaidToManager ?? raw.TipsAndGratuityPaidToManager;

    return {
        merchantId: raw.merchantId ?? raw.MerchantId,
        tipPayoutRoutingMode: raw.tipPayoutRoutingMode ?? raw.TipPayoutRoutingMode ?? '',
        designatedTipPayoutUserId: raw.designatedTipPayoutUserId ?? raw.DesignatedTipPayoutUserId ?? null,
        ...(hasDesignated ? { designatedRecipient: designated } : {}),
        ...(hasEligible ? { eligibleRecipients: eligible } : {}),
        ...(reservedTipsAndGratuity != null
            ? { reservedTipsAndGratuity: Number(reservedTipsAndGratuity) }
            : {}),
        ...(pendingTipsAndGratuity != null
            ? { pendingTipsAndGratuity: Number(pendingTipsAndGratuity) }
            : {}),
        ...(releasableTipsAndGratuity != null
            ? { releasableTipsAndGratuity: Number(releasableTipsAndGratuity) }
            : {}),
        ...(tipsAndGratuityPaidToManager != null
            ? { tipsAndGratuityPaidToManager: Number(tipsAndGratuityPaidToManager) }
            : {}),
        ...(raw.pendingTipsBreakdown != null || raw.PendingTipsBreakdown != null
            ? {
                pendingTipsBreakdown: (raw.pendingTipsBreakdown || raw.PendingTipsBreakdown || [])
                    .map(normalizePendingTipObligationItem)
                    .filter(Boolean),
            }
            : {}),
        ...(raw.tipsPaidToManagerBreakdown != null || raw.TipsPaidToManagerBreakdown != null
            ? {
                tipsPaidToManagerBreakdown: (raw.tipsPaidToManagerBreakdown || raw.TipsPaidToManagerBreakdown || [])
                    .map(normalizeTipsPaidToManagerItem)
                    .filter(Boolean),
            }
            : {}),
        ...(raw.releasableTipsBreakdown != null || raw.ReleasableTipsBreakdown != null
            ? {
                releasableTipsBreakdown: (raw.releasableTipsBreakdown || raw.ReleasableTipsBreakdown || [])
                    .map(normalizeReleasableTipObligationItem)
                    .filter(Boolean),
            }
            : {}),
    };
}

function normalizePreview(raw) {
    if (!raw) return null;
    const config = raw.config || raw.Config || {};
    return {
        merchantId: raw.merchantId ?? raw.MerchantId,
        storeName: raw.storeName ?? raw.StoreName ?? '',
        stripeAccountId: raw.stripeAccountId ?? raw.StripeAccountId ?? '',
        tipPayoutRoutingMode: raw.tipPayoutRoutingMode ?? raw.TipPayoutRoutingMode ?? '',
        availableBalance: Number(raw.availableBalance ?? raw.AvailableBalance ?? 0),
        pendingBalance: Number(raw.pendingBalance ?? raw.PendingBalance ?? 0),
        unpaidTipAmount: Number(raw.unpaidTipAmount ?? raw.UnpaidTipAmount ?? raw.totalUnpaidTipsAndGratuity ?? raw.TotalUnpaidTipsAndGratuity ?? 0),
        totalUnpaidTipsAndGratuity: Number(raw.totalUnpaidTipsAndGratuity ?? raw.TotalUnpaidTipsAndGratuity ?? raw.unpaidTipAmount ?? raw.UnpaidTipAmount ?? 0),
        flatReserveAmount: Number(raw.flatReserveAmount ?? raw.FlatReserveAmount ?? 0),
        percentReserveAmount: Number(raw.percentReserveAmount ?? raw.PercentReserveAmount ?? 0),
        tipsReserveAmount: Number(raw.tipsReserveAmount ?? raw.TipsReserveAmount ?? 0),
        taxCollectedAmount: Number(raw.taxCollectedAmount ?? raw.TaxCollectedAmount ?? 0),
        taxCollectedSinceLocalDate: raw.taxCollectedSinceLocalDate ?? raw.TaxCollectedSinceLocalDate ?? null,
        taxCollectedSource: raw.taxCollectedSource ?? raw.TaxCollectedSource ?? '',
        taxReserveAmount: Number(raw.taxReserveAmount ?? raw.TaxReserveAmount ?? 0),
        totalReserved: Number(raw.totalReserved ?? raw.TotalReserved ?? 0),
        reserveExceedsAvailableBalance: !!(raw.reserveExceedsAvailableBalance ?? raw.ReserveExceedsAvailableBalance),
        stripeHeldAmount: Number(raw.stripeHeldAmount ?? raw.StripeHeldAmount ?? 0),
        stripeHoldEnforced: !!(raw.stripeHoldEnforced ?? raw.StripeHoldEnforced),
        stripeHoldSyncError: raw.stripeHoldSyncError ?? raw.StripeHoldSyncError ?? '',
        autoPayoutEligible: Number(raw.autoPayoutEligible ?? raw.AutoPayoutEligible ?? 0),
        instantAvailableBalance: Number(raw.instantAvailableBalance ?? raw.InstantAvailableBalance ?? 0),
        instantPayoutEligible: Number(raw.instantPayoutEligible ?? raw.InstantPayoutEligible ?? 0),
        instantPayoutAvailable: !!(raw.instantPayoutAvailable ?? raw.InstantPayoutAvailable),
        instantPayoutFeePercent: Number(raw.instantPayoutFeePercent ?? raw.InstantPayoutFeePercent ?? 2),
        instantPayoutFeeFixed: Number(raw.instantPayoutFeeFixed ?? raw.InstantPayoutFeeFixed ?? 0),
        estimatedInstantFee: Number(raw.estimatedInstantFee ?? raw.EstimatedInstantFee ?? 0),
        releasableTipsAndGratuity: raw.releasableTipsAndGratuity ?? raw.ReleasableTipsAndGratuity,
        pendingTipsAndGratuity: raw.pendingTipsAndGratuity ?? raw.PendingTipsAndGratuity,
        config: {
            merchantId: config.merchantId ?? config.MerchantId,
            flatReserveEnabled: !!(config.flatReserveEnabled ?? config.FlatReserveEnabled),
            flatReserveAmount: Number(config.flatReserveAmount ?? config.FlatReserveAmount ?? 0),
            percentReserveEnabled: !!(config.percentReserveEnabled ?? config.PercentReserveEnabled),
            percentReserveValue: Number(config.percentReserveValue ?? config.PercentReserveValue ?? 0),
            reserveTipsEnabled: !!(config.reserveTipsEnabled ?? config.ReserveTipsEnabled),
            reserveTaxEnabled: !!(config.reserveTaxEnabled ?? config.ReserveTaxEnabled),
        },
        previewDetailLevel: raw.previewDetailLevel ?? raw.PreviewDetailLevel ?? '',
    };
}

function scheduleBackgroundFullPayoutPreview(dispatch) {
    window.setTimeout(() => {
        dispatch(loadMerchantPayoutPreviewRequest({ full: true, background: true }));
    }, PAYOUT_FULL_PREVIEW_BACKGROUND_DELAY_MS);
}

export function loadMerchantPayoutPreviewRequest({ full = false, background = false } = {}) {
    return (dispatch) => {
        if (!background) {
            dispatch({ type: MERCHANT_PAYOUT_PREVIEW_RECEIVING });
        }

        const params = { full: !!full };

        return axios.get(
            '/api/stripe/connect/merchant/payout-preview',
            withPayoutTimeout({ params }, { fast: !full && !background }),
        ).then((res) => {
            if (res.data?.status === 'Ok' && res.data.preview) {
                const preview = normalizePreview(res.data.preview);
                const responseDetailLevel = res.data.previewDetailLevel ?? res.data.PreviewDetailLevel ?? '';
                if (!full && !background && responseDetailLevel === 'full') {
                    preview.previewDetailLevel = 'full';
                }

                dispatch({
                    type: MERCHANT_PAYOUT_PREVIEW_RECEIVED,
                    payload: { preview, error: '' },
                });

                if (!full && !background) {
                    scheduleBackgroundFullPayoutPreview(dispatch);
                }

                return;
            }

            if (!background) {
                dispatch({
                    type: MERCHANT_PAYOUT_PREVIEW_ERROR,
                    payload: { error: res.data?.message || 'Failed to load payout preview.' },
                });
            }
        }).catch((err) => {
            if (background) {
                return;
            }

            const status = err?.response?.status;
            let message = err?.response?.data?.message || err.message || 'Failed to load payout preview.';
            if (err.code === 'ECONNABORTED' || /timeout/i.test(message)) {
                message = full
                    ? 'Tip obligation totals are still loading. Balances should already be visible — try Refresh if needed.'
                    : 'Merchant balance preview timed out. Try Refresh. If this keeps happening, the API may be overloaded — wait a moment and retry.';
            } else if (/network error/i.test(message)) {
                message = 'Could not reach the payout API (network error). Check your connection and Refresh.';
            }
            if (status === 404) {
                message = 'Payout API is not available on this server yet. Deploy the latest POSApi build or point REACT_APP_API_BASE_URL to your local API (e.g. http://localhost:5000).';
            }
            dispatch({
                type: MERCHANT_PAYOUT_PREVIEW_ERROR,
                payload: { error: message },
            });
        });
    };
}

export function loadMerchantReserveConfigRequest() {
    return (dispatch) => {
        dispatch({ type: MERCHANT_RESERVE_RECEIVING });

        return axios.get(
            '/api/stripe/connect/merchant/reserve',
            withPayoutTimeout(),
        ).then((res) => {
            if (res.data?.status === 'Ok' && res.data.config) {
                const config = normalizeReserveConfig(res.data.config);
                dispatch({
                    type: MERCHANT_RESERVE_RECEIVED,
                    payload: { config, error: '' },
                });
                return { success: true, config };
            }

            const message = getApiMessage(res.data, 'Failed to load reserve settings.');
            dispatch({ type: MERCHANT_RESERVE_LOAD_ERROR, payload: { error: message } });
            return { success: false, message };
        }).catch((err) => {
            const message = getApiMessage(err?.response?.data, err.message || 'Failed to load reserve settings.');
            dispatch({ type: MERCHANT_RESERVE_LOAD_ERROR, payload: { error: message } });
            return { success: false, message };
        });
    };
}

export function saveMerchantReserveConfigRequest(config) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_RESERVE_SAVING });

        const payload = {
            FlatReserveEnabled: !!config.flatReserveEnabled,
            FlatReserveAmount: toReserveAmount(config.flatReserveAmount),
            PercentReserveEnabled: !!config.percentReserveEnabled,
            PercentReserveValue: toReserveAmount(config.percentReserveValue),
            ReserveTipsEnabled: !!config.reserveTipsEnabled,
            ReserveTaxEnabled: !!config.reserveTaxEnabled,
        };
        const isRelease = !payload.FlatReserveEnabled
            && !payload.PercentReserveEnabled
            && !payload.ReserveTipsEnabled
            && !payload.ReserveTaxEnabled;

        return axios.put('/api/stripe/connect/merchant/reserve', payload, withPayoutTimeout()).then((res) => {
            if (res.data?.status === 'Ok') {
                const savedConfig = normalizeReserveConfig(res.data.config);
                const preview = res.data.preview ? normalizePreview(res.data.preview) : null;
                dispatch({
                    type: MERCHANT_RESERVE_SAVED,
                    payload: {
                        config: savedConfig,
                        preview,
                        error: '',
                    },
                });
                if (savedConfig && (savedConfig.flatReserveEnabled || savedConfig.percentReserveEnabled || savedConfig.reserveTipsEnabled || savedConfig.reserveTaxEnabled)) {
                    if (savedConfig.stripeHoldEnforced) {
                        toast.success(`Reserve saved. Stripe hold enforced for $${Number(savedConfig.stripeHeldAmount || 0).toFixed(2)}.`);
                    } else {
                        toast.warn(
                            savedConfig.stripeHoldSyncError
                                || 'Reserve settings saved, but Stripe could not fully enforce the hold. Check Radar for Platforms / ReserveHold access.',
                        );
                    }
                } else if (savedConfig && Number(savedConfig.stripeHeldAmount || 0) > 0.009) {
                    toast.warn(
                        savedConfig.stripeHoldSyncError
                            || 'Reserve settings cleared, but Stripe still reports a hold. Try Release Reserve again.',
                    );
                } else {
                    toast.success(isRelease ? 'Reserve released.' : 'Reserve settings saved.');
                }
                // Refresh balances after settings persist (save/release no longer returns preview inline).
                scheduleBackgroundFullPayoutPreview(dispatch);
                return;
            }

            const message = res.data?.message
                || (isRelease ? 'Failed to release reserve.' : 'Failed to save reserve settings.');
            dispatch({ type: MERCHANT_RESERVE_SAVE_ERROR, payload: { error: message } });
            toast.error(message);
        }).catch((err) => {
            let message = err?.response?.data?.message
                || err.message
                || (isRelease ? 'Failed to release reserve.' : 'Failed to save reserve settings.');
            if (err.code === 'ECONNABORTED' || /timeout|network error/i.test(message)) {
                message = isRelease
                    ? 'Releasing reserve timed out. Reserve may still have been cleared — use Refresh to verify.'
                    : 'Saving reserve timed out. Reserve settings may still have saved — use Refresh to verify.';
            }
            dispatch({ type: MERCHANT_RESERVE_SAVE_ERROR, payload: { error: message } });
            toast.error(message);
        });
    };
}

export function clearMerchantPayoutError() {
    return { type: MERCHANT_PAYOUT_ERROR_CLEARED };
}

function getApiMessage(data, fallback) {
    return data?.message || data?.Message || fallback;
}

function getPayoutResultMessage(result) {
    if (!result) return '';
    return result.message || result.Message || '';
}

export function createMerchantPayoutRequest(amount, method) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_PAYOUT_CREATING });

        return axios.post('/api/stripe/connect/merchant/payout', {
            Amount: Number(amount) || 0,
            Method: method || 'standard',
        }).then((res) => {
            if (res.data?.status === 'Ok') {
                const result = res.data.result || null;
                dispatch({
                    type: MERCHANT_PAYOUT_CREATED,
                    payload: {
                        preview: normalizePreview(res.data.preview),
                        result,
                        error: '',
                    },
                });
                // Success UI is a large modal in Payout Settings (not a toast).
                return { success: true, result };
            }

            const message = getApiMessage(res.data, 'Failed to create payout.');
            dispatch({ type: MERCHANT_PAYOUT_CREATE_ERROR, payload: { error: message } });
            toast.error(message);
            return { success: false, message };
        }).catch((err) => {
            const message = getApiMessage(err?.response?.data, err.message || 'Failed to create payout.');
            dispatch({ type: MERCHANT_PAYOUT_CREATE_ERROR, payload: { error: message } });
            toast.error(message);
            return { success: false, message };
        });
    };
}

export function loadRecentMerchantBankPayoutsRequest(limit = RECENT_MERCHANT_BANK_PAYOUTS_LIMIT) {
    const cappedLimit = Math.max(1, Math.min(Number(limit) || RECENT_MERCHANT_BANK_PAYOUTS_LIMIT, 50));
    return (dispatch) => {
        dispatch({ type: MERCHANT_RECENT_BANK_PAYOUTS_RECEIVING });

        return axios.get('/api/stripe/connect/merchant/payouts/recent', {
            params: { limit: cappedLimit },
        }).then((res) => {
            if (res.data?.status === 'Ok') {
                const payouts = (res.data.payouts || res.data.Payouts || [])
                    .map(normalizeRecentMerchantBankPayoutRecord)
                    .filter(Boolean);
                dispatch({
                    type: MERCHANT_RECENT_BANK_PAYOUTS_RECEIVED,
                    payload: { payouts, error: '' },
                });
                return { success: true, payouts };
            }

            const message = getApiMessage(res.data, 'Failed to load recent payouts.');
            dispatch({
                type: MERCHANT_RECENT_BANK_PAYOUTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load recent payouts.');
            if (status === 404) {
                message = 'Recent payout history is not available on this server yet. Deploy the latest POSApi build.';
            }
            dispatch({
                type: MERCHANT_RECENT_BANK_PAYOUTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

function normalizeDesignatedRecipientsView(raw) {
    if (!raw) return null;

    const eligible = (raw.eligibleRecipients || raw.EligibleRecipients || [])
        .map(normalizeRecipient)
        .filter(Boolean);
    const designated = normalizeRecipient(raw.designatedRecipient || raw.DesignatedRecipient);

    return {
        merchantId: raw.merchantId ?? raw.MerchantId,
        tipPayoutRoutingMode: raw.tipPayoutRoutingMode ?? raw.TipPayoutRoutingMode ?? '',
        designatedTipPayoutUserId: raw.designatedTipPayoutUserId ?? raw.DesignatedTipPayoutUserId ?? null,
        designatedRecipient: designated,
        eligibleRecipients: eligible,
    };
}

export function loadMerchantTipPayoutDesignatedRecipientsRequest(dateRange = null) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVING });

        return axios.get('/api/stripe/connect/merchant/tip-payout-designated-recipients', withPayoutTimeout({
            params: buildTipPayoutDateQueryParams(dateRange),
        })).then((res) => {
            if (res.data?.status === 'Ok') {
                const partial = normalizeDesignatedRecipientsView(res.data.recipients || res.data.Recipients);
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVED,
                    payload: { partial, error: '' },
                });
                return { success: true, partial };
            }

            const message = getApiMessage(res.data, 'Failed to load designated tip recipients.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load designated tip recipients.');
            if (status === 404) {
                message = 'Designated recipient API is not available on this server yet. Deploy the latest POSApi build.';
            }
            dispatch({
                type: MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

export function loadMerchantTipPayoutManagerSummaryRequest(dateRange = null, options = {}) {
    return loadMerchantTipPayoutSettingsRequest(dateRange, options);
}

export function refreshTipPayoutDataAfterPay(dateRange = null) {
    return async (dispatch) => {
        activeTipPayoutSummaryRequestSignature = null;

        await dispatch(loadMerchantPayoutPreviewRequest());
        await dispatch(loadMerchantTipPayoutManagerSummaryRequest(dateRange, { force: true }));
        await Promise.all([
            dispatch(loadMerchantTipPayoutReleasableBreakdownRequest(dateRange)),
            dispatch(loadMerchantTipPayoutPendingBreakdownRequest(dateRange)),
            dispatch(loadDirectTipPayoutRecipientsRequest(dateRange)),
            dispatch(loadRecentSuccessfulTipPayoutsRequest()),
        ]);
    };
}

export function refreshTipPayoutDataAfterManagerRelease(dateRange = null) {
    return refreshTipPayoutDataAfterPay(dateRange);
}

export function loadRecentSuccessfulTipPayoutsRequest(limit = RECENT_SUCCESSFUL_TIP_PAYOUTS_LIMIT, options = {}) {
    const runBackfill = !!(options?.backfill ?? options?.runBackfill);
    return (dispatch) => {
        dispatch({ type: MERCHANT_RECENT_TIP_PAYOUTS_RECEIVING });

        return axios.get('/api/stripe/connect/payouts/recent-successful', {
            params: {
                limit,
                ...(runBackfill ? { backfill: true } : {}),
            },
        }).then((res) => {
            if (res.data?.status === 'Ok') {
                const payouts = (res.data.payouts || res.data.Payouts || [])
                    .map(normalizeRecentTipPayoutRecord)
                    .filter(Boolean);
                dispatch({
                    type: MERCHANT_RECENT_TIP_PAYOUTS_RECEIVED,
                    payload: { payouts, error: '' },
                });
                return { success: true, payouts };
            }

            const message = getApiMessage(res.data, 'Failed to load recent tip payouts.');
            dispatch({
                type: MERCHANT_RECENT_TIP_PAYOUTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load recent tip payouts.');
            if (status === 404) {
                message = 'Recent tip payout history is not available on this server yet. Deploy the latest POSApi build.';
            }
            dispatch({
                type: MERCHANT_RECENT_TIP_PAYOUTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

export function loadMerchantTipPayoutPendingBreakdownRequest(dateRange = null) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVING });

        return axios.get('/api/stripe/connect/merchant/tip-payout-pending-breakdown', {
            params: buildTipPayoutDateQueryParams(dateRange),
        }).then((res) => {
            if (res.data?.status === 'Ok') {
                const breakdown = (res.data.pendingTipsBreakdown || res.data.PendingTipsBreakdown || [])
                    .map(normalizePendingTipObligationItem)
                    .filter(Boolean);
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVED,
                    payload: { pendingTipsBreakdown: breakdown, error: '' },
                });
                return { success: true, breakdown };
            }

            const message = getApiMessage(res.data, 'Failed to load pending tips breakdown.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load pending tips breakdown.');
            if (status === 404) {
                message = 'Pending tips breakdown API is not available on this server yet. Deploy the latest POSApi build or point REACT_APP_API_BASE_URL to your local API (e.g. http://localhost:5000).';
            }
            dispatch({
                type: MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

export function loadMerchantTipPayoutPaidToManagerBreakdownRequest(dateRange = null) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVING });

        return axios.get('/api/stripe/connect/merchant/tip-payout-paid-to-manager-breakdown', {
            params: buildTipPayoutDateQueryParams(dateRange),
        }).then((res) => {
            if (res.data?.status === 'Ok') {
                const breakdown = (res.data.tipsPaidToManagerBreakdown || res.data.TipsPaidToManagerBreakdown || [])
                    .map(normalizeTipsPaidToManagerItem)
                    .filter(Boolean);
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVED,
                    payload: { tipsPaidToManagerBreakdown: breakdown, error: '' },
                });
                return { success: true, breakdown };
            }

            const message = getApiMessage(res.data, 'Failed to load tips paid to manager breakdown.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(
                err?.response?.data,
                err.message || 'Failed to load tips paid to manager breakdown.',
            );
            if (status === 404) {
                message = 'Tips paid to manager breakdown API is not available on this server yet. Deploy the latest POSApi build.';
            }
            dispatch({
                type: MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

export function loadMerchantTipPayoutReleasableBreakdownRequest(dateRange = null) {
    return (dispatch, getState) => {
        const preview = getState()?.merchantPayout?.preview ?? null;
        dispatch({ type: MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVING });

        return axios.get('/api/stripe/connect/merchant/tip-payout-releasable-breakdown', {
            params: {
                ...buildTipPayoutDateQueryParams(dateRange),
                ...buildTipPayoutLiquidityQueryParams(preview),
            },
        }).then((res) => {
            if (res.data?.status === 'Ok') {
                const breakdown = (res.data.releasableTipsBreakdown || res.data.ReleasableTipsBreakdown || [])
                    .map(normalizeReleasableTipObligationItem)
                    .filter(Boolean);
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVED,
                    payload: { releasableTipsBreakdown: breakdown, error: '' },
                });
                return { success: true, breakdown };
            }

            const message = getApiMessage(res.data, 'Failed to load releasable tips breakdown.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load releasable tips breakdown.');
            if (status === 404) {
                message = 'Releasable tips breakdown API is not available on this server yet. Deploy the latest POSApi build.';
            }
            dispatch({
                type: MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

function buildTipPayoutLiquidityQueryParams(preview) {
    if (!preview) {
        return {};
    }

    const params = {};
    const reserveTipsEnabled = !!preview.config?.reserveTipsEnabled;
    const tipsReserveAmount = toNumber(preview.tipsReserveAmount);
    const availableBalance = toNumber(preview.availableBalance);

    if (reserveTipsEnabled && tipsReserveAmount > 0) {
        params.tipsReserveAmount = tipsReserveAmount;
    } else if (availableBalance > 0) {
        params.availableBalance = availableBalance;
    }

    return params;
}

function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function buildTipPayoutSummaryRequestSignature(dateRange, preview) {
    const dateRangeKey = buildTipPayoutDateRangeKey(dateRange);
    const liquidity = buildTipPayoutLiquidityQueryParams(preview);
    return `${dateRangeKey}|${liquidity.tipsReserveAmount ?? ''}|${liquidity.availableBalance ?? ''}`;
}

export function loadMerchantTipPayoutSettingsRequest(dateRange = null, { includeRecipients = false, force = false } = {}) {
    return (dispatch, getState) => {
        const preview = getState()?.merchantPayout?.preview ?? null;
        const requestSignature = buildTipPayoutSummaryRequestSignature(dateRange, preview);
        const merchantPayoutState = getState()?.merchantPayout;
        const dateRangeKey = buildTipPayoutDateRangeKey(dateRange);
        const cachedSummary = merchantPayoutState?.tipPayoutSummaryCache?.[dateRangeKey];

        if (!force && cachedSummary && cachedSummary.summaryVersion === 6) {
            dispatch({
                type: MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVED,
                payload: {
                    settings: {
                        pendingTipsAndGratuity: cachedSummary.pendingTipsAndGratuity,
                        releasableTipsAndGratuity: cachedSummary.releasableTipsAndGratuity,
                        reservedTipsAndGratuity: cachedSummary.reservedTipsAndGratuity,
                        tipsAndGratuityPaidToManager: cachedSummary.tipsAndGratuityPaidToManager,
                    },
                    dateRangeKey,
                    error: '',
                },
            });
            return Promise.resolve();
        }

        if (!force
            && merchantPayoutState?.tipPayoutSettingsLoading
            && activeTipPayoutSummaryRequestSignature === requestSignature) {
            return Promise.resolve();
        }

        const requestId = ++tipPayoutSettingsRequestSeq;
        activeTipPayoutSummaryRequestSignature = requestSignature;

        dispatch({
            type: MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVING,
            payload: { dateRangeKey },
        });

        return axios.get('/api/stripe/connect/merchant/tip-payout-settings', withPayoutTimeout({
            params: {
                ...buildTipPayoutDateQueryParams(dateRange),
                ...buildTipPayoutLiquidityQueryParams(preview),
                ...(includeRecipients ? {} : { includeRecipients: false }),
            },
        })).then((res) => {
            if (requestId !== tipPayoutSettingsRequestSeq) {
                return;
            }

            if (res.data?.status === 'Ok' && res.data.settings) {
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVED,
                    payload: {
                        settings: normalizeTipPayoutSettings(res.data.settings),
                        preview: res.data.preview ? normalizePreview(res.data.preview) : null,
                        dateRangeKey,
                        error: '',
                    },
                });
                return;
            }

            dispatch({
                type: MERCHANT_TIP_PAYOUT_SETTINGS_ERROR,
                payload: { error: getApiMessage(res.data, 'Failed to load tip payout settings.') },
            });
        }).catch((err) => {
            if (requestId !== tipPayoutSettingsRequestSeq) {
                return;
            }

            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load tip payout settings.');
            if (status === 404) {
                message = 'Tip payout settings API is not available on this server yet. Deploy the latest POSApi build or point REACT_APP_API_BASE_URL to your local API (e.g. http://localhost:5000).';
            }
            dispatch({ type: MERCHANT_TIP_PAYOUT_SETTINGS_ERROR, payload: { error: message } });
        }).finally(() => {
            if (requestId === tipPayoutSettingsRequestSeq) {
                activeTipPayoutSummaryRequestSignature = null;
            }
        });
    };
}

export function saveMerchantTipPayoutSettingsRequest(designatedTipPayoutUserId) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_TIP_PAYOUT_SETTINGS_SAVING });

        const payload = {
            DesignatedTipPayoutUserId: designatedTipPayoutUserId ? Number(designatedTipPayoutUserId) : null,
        };

        return axios.put('/api/stripe/connect/merchant/tip-payout-settings', payload).then((res) => {
            if (res.data?.status === 'Ok') {
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_SETTINGS_SAVED,
                    payload: {
                        settings: normalizeTipPayoutSettings(res.data.settings),
                        preview: res.data.preview ? normalizePreview(res.data.preview) : null,
                        error: '',
                    },
                });
                toast.success('Tip payout recipient saved.');
                return { success: true };
            }

            const message = getApiMessage(res.data, 'Failed to save tip payout recipient.');
            dispatch({ type: MERCHANT_TIP_PAYOUT_SETTINGS_SAVE_ERROR, payload: { error: message } });
            toast.error(message);
            return { success: false, message };
        }).catch((err) => {
            const message = getApiMessage(err?.response?.data, err.message || 'Failed to save tip payout recipient.');
            dispatch({ type: MERCHANT_TIP_PAYOUT_SETTINGS_SAVE_ERROR, payload: { error: message } });
            toast.error(message);
            return { success: false, message };
        });
    };
}

export function payReservedTipsToRecipientRequest(releaseContext, dateRange = null) {
    const resolvedReleaseContext = {
        ...(releaseContext || {}),
        dateRange,
    };

    return (dispatch) => {
        dispatch({ type: MERCHANT_TIP_PAYOUT_PAYING });

        return axios.post(
            '/api/stripe/connect/merchant/tip-payout-to-recipient',
            buildTipPayoutDateRequestBody(dateRange),
        ).then((res) => {
            const apiStatus = res.data?.status;
            const result = res.data.result || null;
            const hasRemaining = Number(result?.amountRemaining ?? result?.AmountRemaining ?? 0) > 0;
            const isPartial = apiStatus === 'Partial' || hasRemaining;
            const isSuccess = apiStatus === 'Ok' || apiStatus === 'Partial' || isPartial;

            if (isSuccess && result) {
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_PAID,
                    payload: {
                        settings: normalizeTipPayoutSettings(res.data.settings),
                        preview: res.data.preview ? normalizePreview(res.data.preview) : null,
                        result,
                        error: '',
                    },
                });
                const toastMessage = getPayoutResultMessage(result)
                    || getApiMessage(res.data, 'Tips paid via Stripe Connect.');
                if (isPartial) {
                    toast.warning(toastMessage || 'Tip release partially completed.');
                } else {
                    toast.success(toastMessage || 'Tips paid via Stripe Connect.');
                }

                try {
                    const paidUpdates = resolvePaidUpdatesFromManagerRelease(res.data, resolvedReleaseContext);
                    if (paidUpdates.length > 0) {
                        dispatch(applyTipObligationPaidUpdates(paidUpdates));
                    }
                } catch (error) {
                    console.warn('Unable to apply local paid status after manager tip release:', error);
                }

                dispatch(refreshTipPayoutDataAfterManagerRelease(resolvedReleaseContext.dateRange));

                return { success: !isPartial, partial: isPartial, result, apiStatus };
            }

            const message = getApiMessage(res.data, 'Failed to pay tips to recipient.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_PAY_ERROR,
                payload: {
                    error: message,
                    settings: res.data.settings ? normalizeTipPayoutSettings(res.data.settings) : null,
                    preview: res.data.preview ? normalizePreview(res.data.preview) : null,
                },
            });
            dispatch(refreshTipPayoutDataAfterManagerRelease(resolvedReleaseContext.dateRange));
            toast.error(message);
            return { success: false, message };
        }).catch((err) => {
            const message = getApiMessage(err?.response?.data, err.message || 'Failed to pay tips to recipient.');
            dispatch({ type: MERCHANT_TIP_PAYOUT_PAY_ERROR, payload: { error: message } });
            dispatch(refreshTipPayoutDataAfterManagerRelease(resolvedReleaseContext.dateRange));
            toast.error(message);
            return { success: false, message };
        });
    };
}

export function markAllOutstandingTipsPaidRequest(reason, dateRange = null) {
    return (dispatch) => {
        dispatch({ type: MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVING });

        return axios.post(
            '/api/stripe/connect/payouts/mark-all-paid',
            {
                reason: reason || undefined,
                ...buildTipPayoutDateRequestBody(dateRange),
            },
            withPayoutTimeout(),
        ).then((res) => {
            const apiStatus = res.data?.status;
            const result = res.data?.result || null;
            const isSuccess = apiStatus === 'Ok' || apiStatus === 'Partial';

            if (isSuccess && result) {
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVED,
                    payload: {
                        settings: normalizeTipPayoutSettings(res.data.settings),
                        preview: res.data.preview ? normalizePreview(res.data.preview) : null,
                        result,
                        error: '',
                    },
                });

                const message = getApiMessage(res.data, result.message || 'Outstanding tips marked as paid.');
                if (apiStatus === 'Partial') {
                    toast.warning(message);
                } else {
                    toast.success(message);
                }

                dispatch(refreshTipPayoutDataAfterPay(dateRange));
                return { success: apiStatus === 'Ok', partial: apiStatus === 'Partial', result, message };
            }

            const message = getApiMessage(res.data, 'Failed to mark outstanding tips as paid.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_MARK_ALL_ERROR,
                payload: { error: message },
            });
            toast.error(message);
            return { success: false, message };
        }).catch((err) => {
            const message = getApiMessage(err?.response?.data, err.message || 'Failed to mark outstanding tips as paid.');
            dispatch({ type: MERCHANT_TIP_PAYOUT_MARK_ALL_ERROR, payload: { error: message } });
            toast.error(message);
            return { success: false, message };
        });
    };
}

export function loadDirectTipPayoutRecipientsRequest(dateRange = null) {
    return (dispatch) => {
        const requestId = ++tipPayoutDirectRecipientsRequestSeq;
        const dateRangeKey = buildTipPayoutDateRangeKey(dateRange);

        dispatch({
            type: MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVING,
            payload: { dateRangeKey },
        });

        return axios.get('/api/stripe/connect/merchant/tip-payout-direct-recipients', {
            params: buildTipPayoutDateQueryParams(dateRange),
        }).then((res) => {
            if (requestId !== tipPayoutDirectRecipientsRequestSeq) {
                return { success: false, stale: true };
            }

            if (res.data?.status === 'Ok') {
                const recipients = (res.data.recipients || res.data.Recipients || [])
                    .map(normalizeRecipient)
                    .filter(Boolean);
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVED,
                    payload: { recipients, dateRangeKey, error: '' },
                });
                return { success: true, recipients };
            }

            const message = getApiMessage(res.data, 'Failed to load direct tip payout recipients.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        }).catch((err) => {
            if (requestId !== tipPayoutDirectRecipientsRequestSeq) {
                return { success: false, stale: true };
            }

            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Failed to load direct tip payout recipients.');
            if (status === 404) {
                message = 'Direct tip payout API is not available on this server yet. Deploy the latest POSApi build.';
            }
            dispatch({
                type: MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_ERROR,
                payload: { error: message },
            });
            return { success: false, message };
        });
    };
}

export function payDirectTipsToEmployeeRequest(userId, recipientDisplayName, dateRange = null, { workPeriodId = null } = {}) {
    return (dispatch) => {
        dispatch({
            type: MERCHANT_TIP_PAYOUT_DIRECT_PAYING,
            payload: { userId, workPeriodId },
        });

        const body = buildTipPayoutDateRequestBody(dateRange, {
            UserId: Number(userId) || 0,
        });
        if (workPeriodId != null) {
            body.WorkPeriodId = Number(workPeriodId) || 0;
        }

        return axios.post(
            '/api/stripe/connect/merchant/tip-payout-to-employee',
            body,
        ).then((res) => {
            const apiStatus = res.data?.status;
            const result = res.data?.result || null;
            const recipients = (res.data.recipients || res.data.Recipients || [])
                .map(normalizeRecipient)
                .filter(Boolean);
            const workPeriodResults = result?.workPeriodResults ?? result?.WorkPeriodResults ?? [];
            const hasRemaining = Array.isArray(workPeriodResults)
                && workPeriodResults.some((item) => Number(item?.amountRemaining ?? item?.AmountRemaining ?? 0) > 0);
            const isPartial = apiStatus === 'Partial' || hasRemaining;
            const isSuccess = apiStatus === 'Ok' || apiStatus === 'Partial' || isPartial;

            if (isSuccess && result) {
                dispatch({
                    type: MERCHANT_TIP_PAYOUT_DIRECT_PAID,
                    payload: {
                        recipients,
                        result,
                        payingUserId: null,
                        error: '',
                    },
                });
                const toastMessage = getPayoutResultMessage(result) || result.message || result.Message;
                if (isPartial) {
                    toast.warning(toastMessage || 'Tip payout partially completed.');
                } else {
                    toast.success(toastMessage || 'Tips paid via Stripe Connect.');
                }

                try {
                    const paidUpdates = buildPaidUpdatesFromDirectEmployeePayResult(result, recipientDisplayName);
                    if (paidUpdates.length > 0) {
                        dispatch(applyTipObligationPaidUpdates(paidUpdates));
                    }
                } catch (error) {
                    console.warn('Unable to apply local paid status after direct tip payout:', error);
                }

                dispatch(refreshTipPayoutDataAfterPay(dateRange));

                return { success: !isPartial, partial: isPartial, result, apiStatus };
            }

            const message = getApiMessage(res.data, 'Failed to pay tips to employee.');
            dispatch({
                type: MERCHANT_TIP_PAYOUT_DIRECT_PAY_ERROR,
                payload: {
                    error: message,
                    recipients,
                    payingUserId: null,
                },
            });
            dispatch(refreshTipPayoutDataAfterPay(dateRange));
            toast.error(message);
            return { success: false, message };
        }).catch((err) => {
            const message = getApiMessage(err?.response?.data, err.message || 'Failed to pay tips to employee.');
            const errorRecipients = (err?.response?.data?.recipients || err?.response?.data?.Recipients || [])
                .map(normalizeRecipient)
                .filter(Boolean);
            dispatch({
                type: MERCHANT_TIP_PAYOUT_DIRECT_PAY_ERROR,
                payload: {
                    error: message,
                    recipients: errorRecipients,
                    payingUserId: null,
                },
            });
            dispatch(refreshTipPayoutDataAfterPay(dateRange));
            toast.error(message);
            return { success: false, message };
        });
    };
}

// ── Admin Force Reclaim (Rebelity Admin only, additive) ────────────────────

function normalizeForceReclaimRow(raw) {
    if (!raw) return null;
    return {
        transferId: raw.transferId ?? raw.TransferId ?? 0,
        userId: raw.userId ?? raw.UserId ?? 0,
        username: raw.username ?? raw.Username ?? '',
        saleId: raw.saleId ?? raw.SaleId ?? '',
        workPeriodId: raw.workPeriodId ?? raw.WorkPeriodId ?? null,
        amount: Number(raw.amount ?? raw.Amount ?? 0),
        previousStatus: raw.previousStatus ?? raw.PreviousStatus ?? '',
        outcome: raw.outcome ?? raw.Outcome ?? '',
        message: raw.message ?? raw.Message ?? '',
    };
}

function normalizeForceReclaimResult(raw) {
    if (!raw) return null;
    return {
        success: !!(raw.success ?? raw.Success),
        dryRun: !!(raw.dryRun ?? raw.DryRun),
        totalReclaimed: Number(raw.totalReclaimed ?? raw.TotalReclaimed ?? 0),
        totalPlanned: Number(raw.totalPlanned ?? raw.TotalPlanned ?? 0),
        rowsProcessed: raw.rowsProcessed ?? raw.RowsProcessed ?? 0,
        rowsReclaimed: raw.rowsReclaimed ?? raw.RowsReclaimed ?? 0,
        rowsSkipped: raw.rowsSkipped ?? raw.RowsSkipped ?? 0,
        rowsFailed: raw.rowsFailed ?? raw.RowsFailed ?? 0,
        merchantPayoutsPaused: !!(raw.merchantPayoutsPaused ?? raw.MerchantPayoutsPaused),
        rows: (raw.rows ?? raw.Rows ?? []).map(normalizeForceReclaimRow).filter(Boolean),
        errorMessage: raw.errorMessage ?? raw.ErrorMessage ?? '',
        auditId: raw.auditId ?? raw.AuditId ?? '',
    };
}

/**
 * Runs an admin force reclaim. dryRun=true previews the plan without Stripe calls.
 * Returns { success, result, message } — no redux state; callers own the UI state.
 */
export function adminForceReclaimRequest({ transferIds, userId, workPeriodId, reason, dryRun = true }) {
    return (dispatch) => {
        return axios.post(
            '/api/stripe/connect/admin/force-reclaim',
            {
                transferIds: transferIds && transferIds.length ? transferIds : undefined,
                userId: userId || undefined,
                workPeriodId: workPeriodId || undefined,
                reason,
                dryRun,
            },
            withPayoutTimeout(),
        ).then((res) => {
            const result = normalizeForceReclaimResult(res.data?.result);
            if (res.data?.status === 'Ok' && result) {
                if (!dryRun) {
                    if (result.rowsFailed > 0) {
                        toast.warning(`Force reclaim finished: ${result.rowsReclaimed} reclaimed, ${result.rowsFailed} failed.`);
                    } else {
                        toast.success(`Force reclaimed $${result.totalReclaimed.toFixed(2)} to the platform (${result.rowsReclaimed} row${result.rowsReclaimed === 1 ? '' : 's'}).`);
                    }
                    dispatch(refreshTipPayoutDataAfterPay(null));
                }
                return { success: true, result };
            }

            const message = getApiMessage(res.data, 'Force reclaim failed.');
            if (!dryRun) toast.error(message);
            return { success: false, result, message };
        }).catch((err) => {
            const status = err?.response?.status;
            let message = getApiMessage(err?.response?.data, err.message || 'Force reclaim failed.');
            if (status === 404) {
                message = 'Force reclaim API is not available on this server yet. Deploy the latest POSApi build.';
            }
            if (!dryRun) toast.error(message);
            return { success: false, message };
        });
    };
}

/**
 * Loads recent persisted force reclaim audit entries for the session merchant.
 */
export function loadForceReclaimAuditRequest(limit = 20) {
    return () => {
        return axios.get('/api/stripe/connect/admin/force-reclaim/audit', { params: { limit } }).then((res) => {
            if (res.data?.status === 'Ok') {
                const records = (res.data.records || res.data.Records || []).map((raw) => ({
                    auditId: raw.auditId ?? raw.AuditId ?? '',
                    adminUserName: raw.adminUserName ?? raw.AdminUserName ?? '',
                    reason: raw.reason ?? raw.Reason ?? '',
                    attemptedAtUtc: raw.attemptedAtUtc ?? raw.AttemptedAtUtc ?? '',
                    totalReclaimed: Number(raw.totalReclaimed ?? raw.TotalReclaimed ?? 0),
                    rowsReclaimed: raw.rowsReclaimed ?? raw.RowsReclaimed ?? 0,
                    rowsSkipped: raw.rowsSkipped ?? raw.RowsSkipped ?? 0,
                    rowsFailed: raw.rowsFailed ?? raw.RowsFailed ?? 0,
                }));
                return { success: true, records };
            }
            return { success: false, records: [], message: getApiMessage(res.data, 'Failed to load force reclaim history.') };
        }).catch((err) => {
            return { success: false, records: [], message: getApiMessage(err?.response?.data, err.message || 'Failed to load force reclaim history.') };
        });
    };
}
